import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  oktaLogin,
  handleOktaCallback,
  logout,
  getCurrentUser,
  handleIdPInitiated,
  refreshAccessToken,
  isAuthenticated,
  type BackendAuthResponse,
} from '../api/unified-auth'

// Query keys for React Query
export const authKeys = {
  all: ['auth'] as const,
  status: () => [...authKeys.all, 'status'] as const,
  user: () => [...authKeys.all, 'user'] as const,
}

// Hook to check authentication status
export function useAuthStatus() {
  return useQuery({
    queryKey: authKeys.status(),
    queryFn: isAuthenticated,
    staleTime: 2 * 60 * 1000, // 2 minutes (shorter since tokens are 15min)
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Hook to get current user info
export function useCurrentUser() {
  const { data: isAuth } = useAuthStatus()

  return useQuery({
    queryKey: authKeys.user(),
    queryFn: getCurrentUser,
    enabled: isAuth,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry if authentication failed
      if (error?.message?.includes('token') || error?.message?.includes('authentication')) {
        return false
      }
      return failureCount < 2
    },
  })
}

// Hook for Okta login (redirects to Okta)
export function useOktaLogin() {
  return useMutation({
    mutationFn: oktaLogin,
    onError: error => {
      console.error('Okta login failed:', error)
    },
  })
}

// Hook for handling Okta callback
export function useOktaCallback() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: handleOktaCallback,
    onSuccess: (data: BackendAuthResponse) => {
      // Update auth status
      queryClient.setQueryData(authKeys.status(), true)
      queryClient.setQueryData(authKeys.user(), { user: data.user })

      // Invalidate all auth queries to refresh
      queryClient.invalidateQueries({ queryKey: authKeys.all })
    },
    onError: error => {
      console.error('Okta callback failed:', error)
      queryClient.setQueryData(authKeys.status(), false)
      queryClient.removeQueries({ queryKey: authKeys.user() })
    },
  })
}

// Hook for logout
export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      // Clear all auth data
      queryClient.setQueryData(authKeys.status(), false)
      queryClient.removeQueries({ queryKey: authKeys.user() })

      // Clear all queries to prevent stale data
      queryClient.clear()
    },
    onError: error => {
      console.error('Logout failed:', error)
      // Even if logout failed on backend, clear local state
      queryClient.setQueryData(authKeys.status(), false)
      queryClient.removeQueries({ queryKey: authKeys.user() })
    },
  })
}

// Hook for handling IdP-initiated login (from Okta tile)
export function useIdPInitiatedLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: handleIdPInitiated,
    onSuccess: (data: BackendAuthResponse | null) => {
      if (data) {
        // Update auth status
        queryClient.setQueryData(authKeys.status(), true)
        queryClient.setQueryData(authKeys.user(), { user: data.user })

        // Invalidate all auth queries to refresh
        queryClient.invalidateQueries({ queryKey: authKeys.all })
      }
    },
    onError: error => {
      console.error('IdP-initiated login failed:', error)
    },
  })
}

//TODO: this is not used anywhere, we should remove it
// Hook for token refresh
export function useTokenRefresh() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: refreshAccessToken,
    onSuccess: data => {
      // Update auth status
      queryClient.setQueryData(authKeys.status(), true)
      if (data.user) {
        queryClient.setQueryData(authKeys.user(), { user: data.user })
      }

      // Invalidate auth queries to refresh
      queryClient.invalidateQueries({ queryKey: authKeys.all })
    },
    onError: error => {
      console.error('Token refresh failed:', error)
      // Clear auth state on refresh failure
      queryClient.setQueryData(authKeys.status(), false)
      queryClient.removeQueries({ queryKey: authKeys.user() })
    },
  })
}
